<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            class="receivables-report"
            ref="grid"
            :defaultColDef="defaultColDef"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :getRowStyle="getRowStyle"
            :isSearch="false"
            :isAutoWidth="true"
            :statusBar="statusBar"
            gridTheme="ag-theme-alpine-report"
        >
            <div class="d-flex align-items-baseline mr-1 mb-1 receivables-date">
                <form-datepicker
                    v-model="recordedBy"
                    @input="fetchData"
                    variant="primary"
                    :hide-header="true"
                    :responsiveness="isMobile"
                    :display-format="userUnicodeDateFormat"
                />
            </div>
            <div
                class="d-flex align-items-baseline mr-1 mb-1 filter-properties"
            >
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"

            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="pageOrientationOptions"
                variant="primary"
            >
                {{ pageOrientationLabel }}
            </responsive-dropdown>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import grid from '../generic/grid/grid.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import FormDatepicker from '../generic/form-datepicker/form-datepicker.vue';
import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import ReportSummaryBar from './report-summary-bar.vue';
import { mapGetters } from 'vuex';
import {
    REPORT_RECEIVABLES_ENDPOINT,
    REPORT_RECEIVABLES_EXCEL_ENDPOINT,
    REPORT_RECEIVABLES_PROPERTIES_ENDPOINT,
} from '../../endpoints';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { isMediumScreen } from '../../utils/screens';
import { escapeHTML } from '../../utils/strings';
import { toAmountDisplay } from '../../utils/numbers';
import { PAGE_ORIENTATION_LABELS, PAGE_ORIENTATION } from '../../constants';
import ReportSettingsModal, {
    MULTI_SELECT_INPUT,
    DATE_PICKER_INPUT,
} from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    name: 'ReceivablesReport',
    components: {
        Page,
        grid,
        FormDatepicker,
        FormSearchMultiselect,
        ExportToExcelButton,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        reportSummaryBar: ReportSummaryBar,
        exportToPDFButton,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            busy: null,
            tabs: [
                {
                    title: 'Receivables Report',
                    route: '/app/reports/receivables/',
                    active: true,
                },
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            recordedBy: null,
            rowData: [],
            gridApi: null,
            defaultColDef: {
                minWidth: 0,
                sortable: false,
            },
            filterProperties: [],
            filterPropertyOptions: [],
            pageOrientationLabel: PAGE_ORIENTATION_LABELS[0],
            pageOrientation: PAGE_ORIENTATION[0],
            receivablesTotal: {},
            statusBar: null,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal'
        };
    },
    beforeMount() {
        this.recordedBy = format(new Date(), this.dateRequestFormat);
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    field: 'start_date',
                    headerName: 'Inv Start',
                    minWidth: 150,
                    valueGetter: ({ data }) => {
                        if (data) {
                            if (data.referrer === 'title_row') {
                                return data.rowTitle;
                            } else if (data.referrer === 'data_row') {
                                return format(new Date(data.start_date), this.userUnicodeDateFormat);
                            }
                        }
                        return '';
                    },
                },
                {
                    field: 'end_date',
                    headerName: 'Inv End',
                    type: 'dateColumn',
                    minWidth: 150,
                },
                {
                    field: 'invoice_pk',
                    headerName: 'Inv #',
                    minWidth: 80,
                    maxWidth: 80,
                    cellRenderer: (params) => {
                        const { node, value } = params;
                        if (!node.rowPinned && value) {
                            return `<a target="_self" href="/app/reservation/edit/invoice/${value}">${value}</a>`
                        }
                        return ''
                    }
                },
                {
                    field: 'booking_method',
                    headerName: 'Source',
                    minWidth: 130,
                },                
                {
                    field: 'p_names',
                    headerName: 'Property',
                    minWidth: 200,
                    autoHeight: true,
                    valueGetter: ({ data }) =>
                        data &&
                        (data.properties || [])
                            .map(
                                property => this.shortPropertyName(property)
                            )
                            .join('; '),
                    cellRenderer: ({ data }) =>
                        data &&
                        (data.p_names || [])
                            .map((property) =>
                                escapeHTML(property)
                            )
                            .join('<br/>'),
                },
                {
                    field: 'guest_name',
                    headerName: 'Guest Name',
                    minWidth: 160,
                },
                {
                    field: 'cc_on_file',
                    headerName: 'CC on File?',
                    minWidth: 100,
                    valueGetter: ({ data, node }) => {
                        if (data && data.referrer === 'data_row' && !node.rowPinned) {
                            return data.cc_on_file ? 'Yes' : 'No';
                        }
                        return '';
                    }
                },
                {
                    field: 'charges',
                    headerName: 'Charges',
                    minWidth: 85,
                    cellClass: 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                    valueGetter: ({ data, node, value }) => {
                        if (data && !node.rowPinned) {
                            if (!data.split_2) {
                                return toAmountDisplay({
                                    amount: data.charges,
                                    currency: this.userCurrencySymbol,
                                });
                            }
                            return 'N/A';
                        }
                        if (node.rowPinned) {
                            return toAmountDisplay({
                                amount: this.receivablesTotal?.charges || 0,
                                currency: this.userCurrencySymbol,
                            });
                        }
                        return '';
                    }
                },
                {
                    field: 'other_deposit',
                    headerName: 'Other Deposit',
                    minWidth: 110,
                    cellClass: 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                    valueGetter: ({ data, node, value }) => {
                        if (data && !node.rowPinned) {
                            if (!data.split_2) {
                                return toAmountDisplay({
                                    amount: data.other_deposit,
                                    currency: this.userCurrencySymbol,
                                });
                            }
                            return 'N/A';
                        }
                        if (node.rowPinned) {
                            return toAmountDisplay({
                                amount: this.receivablesTotal?.other_deposit || 0,
                                currency: this.userCurrencySymbol,
                            });
                        }
                        return '';
                    }
                },
                {
                    field: 'total',
                    headerName: 'Total',
                    minWidth: 85,
                    cellClass: 'ag-right-aligned-cell',
                    cellStyle: {
                        borderRight: '1px solid black',
                    },
                    headerClass: 'ag-right-aligned-header',
                    valueGetter: ({ data, node, value }) => {
                        if (data && !node.rowPinned) {
                            if (!data.split_2) {
                                return toAmountDisplay({
                                    amount: data.total,
                                    currency: this.userCurrencySymbol,
                                });
                            }
                            return 'N/A';
                        }
                        if (node.rowPinned) {
                            return toAmountDisplay({
                                amount: this.receivablesTotal?.total || 0,
                                currency: this.userCurrencySymbol,
                            });
                        }
                        return '';
                    }
                },
                {
                    field: 'paid',
                    headerName: 'Paid',
                    minWidth: 85,
                    cellClass: 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                    valueGetter: ({ data, node, value }) => {
                        if (data && !node.rowPinned) {
                            if (!data.split_2) {
                                return toAmountDisplay({
                                    amount: data.paid,
                                    currency: this.userCurrencySymbol,
                                });
                            }
                            return 'N/A';
                        }
                        if (node.rowPinned) {
                            return toAmountDisplay({
                                amount: this.receivablesTotal?.paid || 0,
                                currency: this.userCurrencySymbol,
                            });
                        }
                        return '';
                    }
                },
                {
                    field: 'owed',
                    headerName: 'Owed',
                    minWidth: 85,
                    cellClass: 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                    valueGetter: ({ data, node, value }) => {
                        if (data && !node.rowPinned) {
                            return toAmountDisplay({
                                amount: data.owed,
                                currency: this.userCurrencySymbol,
                            }) + (data.split_1 || data.split_2 ? '*' : '');
                        }
                        if (node.rowPinned) {
                            return toAmountDisplay({
                                amount: this.receivablesTotal?.owed || 0,
                                currency: this.userCurrencySymbol,
                            });
                        }
                        return '';
                    }
                },
                {
                    field: 'balance_due_date',
                    headerName: 'Owed Date',
                    type: 'dateColumn',
                    minWidth: 120,
                    autoWidthExclude: true,
                },
            ];
        },
        todayDate() {
            return format(new Date(), this.userUnicodeDateFormat);
        },
        pageOrientationOptions() {
            return [
                {
                    label: PAGE_ORIENTATION_LABELS[0],
                    click: () => {
                        this.pageOrientationLabel = PAGE_ORIENTATION_LABELS[0];
                        this.pageOrientation = PAGE_ORIENTATION[0];
                    },
                },
                {
                    label: PAGE_ORIENTATION_LABELS[1],
                    click: () => {
                        this.pageOrientationLabel = PAGE_ORIENTATION_LABELS[1];
                        this.pageOrientation = PAGE_ORIENTATION[1];
                    },
                },
            ]
        },
        exportPdfHref() {
            let url = `/app/reports/receivables/row-data/pdf/`
            url += `?recorded_by=${this.recordedBy}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            return url
        },
        exportExcelHref() {
            let url = REPORT_RECEIVABLES_EXCEL_ENDPOINT();
            url += `?recorded_by=${this.recordedBy}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            return url;
        },
        pinnedBottomRowData() {
            return [
                {
                    'charges': toAmountDisplay({
                        amount: this.receivablesTotal?.charges || 0,
                        currency: this.userCurrencySymbol,
                    }),
                    'total': toAmountDisplay({
                        amount: this.receivablesTotal?.total || 0,
                        currency: this.userCurrencySymbol,
                    }),
                    'paid': toAmountDisplay({
                        amount: this.receivablesTotal?.paid || 0,
                        currency: this.userCurrencySymbol,
                    }),
                    'owed': toAmountDisplay({
                        amount: this.receivablesTotal?.owed || 0,
                        currency: this.userCurrencySymbol,
                    }),
                    'other_deposit': toAmountDisplay({
                        amount: this.receivablesTotal?.other_deposit || 0,
                        currency: this.userCurrencySymbol,
                    }),
                }
            ]
        },
        isMobile() {
            return !this.isMediumScreen();
        },
        reportSettings() {
            return {
                name: 'Receivables',
                report_sysname: 'RECEIVABLES',
                settings: [
                    {
                        type: DATE_PICKER_INPUT,
                        value: format(new Date(), 'yyyy-MM-dd'),
                        label: 'Recorded By',
                        name: 'recorded_by',
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        value: [],
                        label: 'Only for Properties',
                        name: 'properties',
                        options: this.filterPropertyOptions,
                        placeholder: 'All Properties'
                    }
                ],
            };
        },
    },
    methods: {
        isMediumScreen,
        prepareRowData(arr, rowName, totalValues) {
            const { owed, charges, other_deposit, total, paid } = totalValues;
            const formattedRowName = rowName.split(', ').join('_')
            return [
                {
                    referrer: 'title_row',
                    rowTitle: rowName,
                    id: `${formattedRowName}_title_row`,
                },
                ...arr.map((item, index) => {
                    return {
                        ...item,
                        referrer: `data_row`,
                        rowTitle: rowName,
                        id: `${formattedRowName}_${index}`,
                    };
                }),
                {
                    owed,
                    other_deposit,
                    charges,
                    total,
                    paid,
                    referrer: `total_row`,
                    rowTitle: rowName,
                    id: `${formattedRowName}_total_row`,
                },
            ];
        },
        async fetchProperties() {
            const { data } = await API.get(REPORT_RECEIVABLES_PROPERTIES_ENDPOINT());
            this.filterPropertyOptions = data
                .map((item) => ({ value: item.property_id, text: item.name, disabled: item.disabled }))
        },
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_RECEIVABLES_ENDPOINT(),
                    {
                        params: {
                            recorded_by: this.recordedBy,
                            properties: this.filterProperties.join(','),
                        },
                    }
                );
                const { data: receivablesData, total, pdf_cache_key } = data
                this.pdfCacheKey = pdf_cache_key;
                this.receivablesTotal = { ...total }
                this.rowData = []
                Object.entries(receivablesData).forEach(([key, value]) => {
                    const { owed, charges, other_deposit, total, paid } = value;
                    const rowDataArray = this.prepareRowData(
                        value.guest_infos,
                        value.period,
                        { owed, charges, other_deposit, total, paid }
                    )
                    this.rowData = [...this.rowData, ...rowDataArray]
                })
                this.$nextTick(() => {
                    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
                        if (node.data && node.data.referrer !== 'data_row') {
                            node.pdfRowStyle = {
                                bold: true
                            };
                        }
                    })
                });
            } catch (e) {
                this.errorToast({ text: "Could not fetch data" });
            } finally {
                this.busy = false
            }
        },
        onResize() {
            const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance = statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        getRowStyle({ data, node }) {
            if (!data || node.rowPinned) {
                return {
                    fontWeight: 'bold',
                };
            } else if (data && data.referrer !== 'data_row') {
                if (data.referrer === 'title_row') {
                    return {
                        fontWeight: 'bold',
                    };
                } else {
                    return {
                        fontWeight: 'bold',
                        borderTop: '1px solid black'
                    };
                }
            }
        }
    },
    mounted() {
        this.fetchProperties();
        this.recordedBy = format(new Date(), this.dateRequestFormat);
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() { 
        window.removeEventListener('resize', this.onResize);
    },
    watch: {
        filterProperties: {
            deep: true,
            handler() {
                this.fetchData();
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.receivables-date,
.filter-properties {
    > fieldset {
        margin: 0 !important;
    }
}
</style>